import React, { Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users/Users';
import Vendors from './pages/Vendors/Vendors';
import Stores from './pages/Stores/Stores';
import Products from './pages/Products/Products';
import Categories from './pages/Categories/Categories';
import Subcategories from './pages/Subcategories/Subcategories';
import Inventory from './pages/Inventory/Inventory';
import Orders from './pages/Orders/Orders';
import Banners from './pages/Banners/Banners';
import Variants from './pages/Variants/Variants';
import Coupons from './pages/Coupons/Coupons';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/App.css';

// Error Boundary Component
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Error caught by boundary:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
                    <div className="text-center p-4">
                        <h2 className="text-danger">Something went wrong</h2>
                        <p className="text-muted">{this.state.error?.message || 'An unexpected error occurred'}</p>
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => {
                                localStorage.clear();
                                window.location.href = '/login';
                            }}
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Protected Route component with loading state
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
                <div className="text-center">
                    <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading application...</p>
                </div>
            </div>
        );
    }

    return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public Route component
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

function App() {
    return (
        <ErrorBoundary>
            <AuthProvider>
                <Router>
                    <div className="App">
                        <Routes>
                            <Route
                                path="/login"
                                element={
                                    <PublicRoute>
                                        <Login />
                                    </PublicRoute>
                                }
                            />

                            <Route
                                path="/"
                                element={
                                    <ProtectedRoute>
                                        <Layout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route index element={<Navigate to="/dashboard" replace />} />
                                <Route path="dashboard" element={<Dashboard />} />
                                <Route path="users" element={<Users />} />
                                <Route path="vendors" element={<Vendors />} />
                                <Route path="stores" element={<Stores />} />
                                <Route path="products" element={<Products />} />
                                <Route path="categories" element={<Categories />} />
                                <Route path="subcategories" element={<Subcategories />} />
                                <Route path="inventory" element={<Inventory />} />
                                <Route path="orders" element={<Orders />} />
                                <Route path="banners" element={<Banners />} />
                                <Route path="variants" element={<Variants />} />
                                <Route path="coupons" element={<Coupons />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                    </div>
                </Router>
            </AuthProvider>
        </ErrorBoundary>
    );
}

export default App;